import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Link } from "react-router-dom";
import { TrendingUp, Award, Plane, Users, ShieldCheck, Sparkles, Trophy, Gift } from "lucide-react";

const MONTHLY_BONUS_TIERS = [
  { tier:  1, high_sp:     200, match_sp:    100, bonus:    1500 },
  { tier:  2, high_sp:     600, match_sp:    300, bonus:    4500 },
  { tier:  3, high_sp:    1200, match_sp:    600, bonus:    9000 },
  { tier:  4, high_sp:    2400, match_sp:   1200, bonus:   18000 },
  { tier:  5, high_sp:    4800, match_sp:   2400, bonus:   36000 },
  { tier:  6, high_sp:   12000, match_sp:   6000, bonus:   90000 },
  { tier:  7, high_sp:   24000, match_sp:  12000, bonus:  180000 },
  { tier:  8, high_sp:   48000, match_sp:  24000, bonus:  360000 },
  { tier:  9, high_sp:   96000, match_sp:  48000, bonus:  720000 },
  { tier: 10, high_sp:  192000, match_sp:  96000, bonus: 1440000 },
  { tier: 11, high_sp:  384000, match_sp: 192000, bonus: 2880000 },
  { tier: 12, high_sp:  768000, match_sp: 384000, bonus: 4000000 },
  { tier: 13, high_sp: 1536000, match_sp: 768000, bonus: 6000000 },
];

const RANKS = [
  { rank: "Executive",        pairs: 15,    child: "—",             perks: "Batch · Certificate · Memento · Website Listing · One-time gift" },
  { rank: "Sr. Executive",    pairs: 25,    child: "—",             perks: "Batch · Certificate · Memento · Website Listing · One-time gift" },
  { rank: "Silver",           pairs: 50,    child: "—",             perks: "+ National Trip (N.T.) · One-time cash prize" },
  { rank: "Gold",             pairs: 100,   child: "—",             perks: "+ National Trip (N.T.) · One-time cash prize" },
  { rank: "Pearl",            pairs: 200,   child: "—",             perks: "+ International Trip (I.T.) + Car Fund" },
  { rank: "Emerald",          pairs: 400,   child: "2 × Silver",    perks: "+ I.T. + Car Fund" },
  { rank: "Sapphire",         pairs: 600,   child: "6 × Silver",    perks: "+ I.T. + Car Fund" },
  { rank: "Ruby",             pairs: 960,   child: "6 × Gold",      perks: "+ I.T. + Car Fund" },
  { rank: "Platinum",         pairs: 1560,  child: "10 × Gold",     perks: "+ I.T. + Car Fund" },
  { rank: "Diamond",          pairs: 2400,  child: "12 × Gold",     perks: "+ I.T. + Car Fund" },
  { rank: "Royal",            pairs: 3900,  child: "12 × Pearl",    perks: "+ I.T. + Car Fund" },
  { rank: "Crown-Ambassador", pairs: 6000,  child: "10 × Emerald",  perks: "+ I.T. + Car Fund" },
  { rank: "Imperial-Crown",   pairs: 9000,  child: "8 × Sapphire",  perks: "+ I.T. + Car Fund" },
  { rank: "Global-Crown",     pairs: 14000, child: "12 × Sapphire", perks: "+ I.T. + Car Fund · Legacy Award" },
];

const INCOMES = [
  { i: Users,       l: "Retail Profit",       d: "MRP − Associate Price on every product you sell." },
  { i: Sparkles,    l: "Self Retail Income",  d: "₹500 for every 50 SP of personal sales." },
  { i: Award,       l: "1:1 Weekly Matching", d: "₹20 per matched SP · stronger leg carries forward each week." },
  { i: TrendingUp,  l: "Startup Income",      d: "One-time highest slab: 20:20→₹400 · 30:30→₹600 · 50:50→₹1,000 · 100:100→₹2,000." },
  { i: ShieldCheck, l: "Booster",             d: "₹500 instantly on 50 SP on any leg — no match needed." },
  { i: Gift,        l: "Monthly Bonus",       d: "2:1 SP-ratio bonus paid monthly across 13 levels — up to ₹60 lakh at Level 13." },
  { i: Trophy,      l: "Rank Rewards",        d: "14 leadership ranks · Batch, Certificate, Memento, One-time gift/cash." },
  { i: Plane,       l: "Trips & Funds",       d: "National, International & Exotic trips · Bike, Car & House funds by rank." },
];

function formatINR(n) {
  return "₹" + n.toLocaleString("en-IN");
}

export default function Plan() {
  return (
    <div className="min-h-screen bg-sand">
      <Navbar />

      <section className="bg-navy text-white py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-xs uppercase tracking-[0.4em] text-gold mb-4">Samara Business Plan</div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl">Earn eight ways. Live one dream.</h1>
          <p className="mt-6 max-w-2xl text-white/70 leading-relaxed">
            A 1:1 binary plan with eight active income streams, 12 Boons Ladder milestones, and 14 leadership ranks — built for every direct seller.
          </p>
        </div>
      </section>

      {/* Income streams */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
        <div className="text-center max-w-xl mx-auto mb-12">
          <div className="text-xs uppercase tracking-[0.4em] text-gold mb-3">Income Streams</div>
          <h2 className="font-serif text-3xl md:text-4xl text-navy">Eight ways you earn</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {INCOMES.map((x) => (
            <div key={x.l} className="bg-white border border-border hover:border-gold/40 transition-colors p-6 rounded-sm">
              <div className="w-12 h-12 bg-cream grid place-items-center border border-gold/30 mb-4 rounded-sm">
                <x.i className="text-gold" size={22} />
              </div>
              <div className="font-serif text-xl text-navy">{x.l}</div>
              <div className="text-sm text-navy/60 mt-2 leading-relaxed">{x.d}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Monthly Bonus (2:1 SP ratio) */}
      <section className="paper-bg py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-xl mx-auto mb-10">
            <div className="text-xs uppercase tracking-[0.4em] text-gold mb-3">Monthly Bonus</div>
            <h2 className="font-serif text-3xl md:text-4xl text-navy">13 levels · 2:1 SP ratio</h2>
            <p className="text-sm text-navy/60 mt-3">Paid each month at the highest level your team reaches. Higher side and matching side track separately.</p>
          </div>
          <div className="overflow-x-auto bg-white border border-border rounded-sm">
            <table className="w-full text-sm min-w-[520px]">
              <thead className="bg-navy text-white">
                <tr>
                  {["Tier", "Higher-side SP", "Matching-side SP", "Monthly Bonus"].map((h) => (
                    <th key={h} className="text-left px-4 py-3 text-xs uppercase tracking-widest font-medium">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {MONTHLY_BONUS_TIERS.map((r) => (
                  <tr key={r.tier} data-testid={`mbonus-tier-${r.tier}`} className="border-b border-border hover:bg-cream/50 transition-colors">
                    <td className="px-4 py-3 text-navy font-semibold">T{r.tier}</td>
                    <td className="px-4 py-3 text-navy/70">{r.high_sp.toLocaleString("en-IN")}</td>
                    <td className="px-4 py-3 text-navy/70">{r.match_sp.toLocaleString("en-IN")}</td>
                    <td className="px-4 py-3 font-semibold text-gold-deep">{formatINR(r.bonus)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Rank ladder */}
      <section className="py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-xl mx-auto mb-10">
            <div className="text-xs uppercase tracking-[0.4em] text-gold mb-3">Leadership Ranks</div>
            <h2 className="font-serif text-3xl md:text-4xl text-navy">14 ranks. Real rewards.</h2>
            <p className="text-sm text-navy/60 mt-3">Every rank comes with a batch, certificate, memento, website listing and a one-time gift. Higher ranks unlock trips and Bike/Car/House funds.</p>
          </div>
          <div className="overflow-x-auto bg-white border border-border rounded-sm">
            <table className="w-full text-sm min-w-[720px]">
              <thead className="bg-navy text-white">
                <tr>
                  {["Rank", "Pairs / year", "Child Achievers", "Perks"].map((h) => (
                    <th key={h} className="text-left px-4 py-3 text-xs uppercase tracking-widest font-medium">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {RANKS.map((r) => (
                  <tr key={r.rank} data-testid={`rank-${r.rank.toLowerCase().replaceAll(" ", "-")}`} className="border-b border-border hover:bg-cream/50 transition-colors">
                    <td className="px-4 py-3 text-navy font-semibold whitespace-nowrap">{r.rank}</td>
                    <td className="px-4 py-3 text-gold-deep font-medium">{r.pairs.toLocaleString("en-IN")}</td>
                    <td className="px-4 py-3 text-navy/60">{r.child}</td>
                    <td className="px-4 py-3 text-navy/70">{r.perks}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20 text-center">
        <h2 className="font-serif text-3xl md:text-4xl text-navy">Ready to start?</h2>
        <p className="text-navy/60 mt-3">Register free, pick a package, and unlock your IBA ID.</p>
        <div className="mt-8 flex flex-wrap justify-center gap-4">
          <Link to="/register" data-testid="plan-cta-register" className="bg-gold text-navy px-7 py-3 hover:bg-gold-deep rounded-sm font-medium">Register</Link>
          <Link to="/packages" data-testid="plan-cta-packages" className="border border-navy text-navy px-7 py-3 hover:bg-navy hover:text-white transition-colors rounded-sm">View Packages</Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
